import { Component, OnInit, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { PatientService } from '../patient.service';
import { IconRegistryService } from '../../../utilities/icon-registry.service';
import { UiService } from '../../../services/ui.service';
import { DialogService } from '../../../services/dialog.service';
import { ContactCommsDataModel } from '../../../models/person/contact-comms-data.model';
import { EnumValidValueItemModel } from '../../../models/enum/enum-valid-value-item.model';
import { StaticDataService } from '../../../services/static-data.service';
import { AddressDataModel } from '../../../models/person/address-data.model';
import { PatientDialogDataModel } from '../patient-dialog-data.model';
import { TdoAccordionComponent } from '../../../tdo-controls/common/tdo-accordion/tdo-accordion.component';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { DoctorsService } from 'src/app/services/doctors.service';

export interface IComm {
    typeDescription: string;
    addrDescription: string;
    enumCommMethodID: string;
    enumCommLocationID: string;
    enumCountryID: string;
    Address: string;
    isInternational: boolean;
    isPreferred: boolean;
}

@Component({
    selector: 'app-patient-comms',
    templateUrl: './patient-comms.component.html',
    styleUrls: ['./patient-comms.component.scss']
})
export class PatientCommsComponent implements OnInit {
    @Input() data: ContactCommsDataModel[];
    @Input() mode: string;
    @Input() type = '';
    @Input() entityType: string;
    @Input() entity: any;
    @Input() entityId: string;
    @Input() isAutoOpenFirstItem: boolean = false;
    @Output() dataEvent = new EventEmitter();
    @Output() errorEvent = new EventEmitter();

    @ViewChild('accordion') accordion: TdoAccordionComponent;

    isLoading: boolean = false;
    phoneMask: any[] = ['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/];
    commMethodEnums: EnumValidValueItemModel[];

    constructor(
        private readonly patientService: PatientService,
        private readonly staticDataService: StaticDataService,
        iconRegistry: IconRegistryService,
        private readonly dialogService: DialogService,
        private readonly doctorsService: DoctorsService,
        public uiService: UiService) {
        iconRegistry.registerIcon('email', './assets/icons/icons8_email.svg');
        iconRegistry.registerIcon('phone', './assets/icons/icons8-phone.svg');
        iconRegistry.registerIcon('mobile', './assets/icons/icons8-mobile.svg');
        iconRegistry.registerIcon('fax', './assets/icons/icons8-fax.svg');
    }

    ngOnInit() {
        if (this.data && this.data.length === 0) {
            this.addNewComm();
        }

        this.commMethodEnums = this.staticDataService.getValidValueItemsByValidValueName('enumCommMethod');
        for (const comm of this.data) {
            const enu = this.commMethodEnums.find(p => p.ValidValueItemId === comm.enumCommMethodID);
            if (enu) {
                comm.typeDescription = enu.ItemDisplayValue;
            }
        }
    }

    ngAfterViewInit(): void {
        if (this.isAutoOpenFirstItem) {
            const ivl = setInterval(() => {
                if (this.accordion.items.length === 0) {
                    return;
                }
                clearInterval(ivl);
                if (this.accordion.items.filter(i => i.isExpanded).length === 0)
                    this.accordion.items.get(0).isExpanded = true;
            }, 200);
        }
    }

    deleteComm(event: MouseEvent, communicationObj: ContactCommsDataModel) {
        event.stopImmediatePropagation();
        this.dialogService.openConfirmDialog('Are you sure you want to delete this communication method?').subscribe((result) => {
            if (result) {
                if (communicationObj.CommMethID.length > 0) {
                    this.patientService.deleteCommunction(communicationObj.CommMethID).subscribe((res) => {
                        const idx = this.data.findIndex(p => p.CommMethID === communicationObj.CommMethID);
                        if (idx > -1) {
                            this.data.splice(idx, 1);
                        }
                    });
                } else {
                    const idx = this.data.findIndex(p => p === communicationObj);
                    if (idx > -1) {
                        this.data.splice(idx, 1);
                    }

                }
            }
        });
    }

    addNewComm() {
        if (this.accordion) {
            const sub = this.accordion.childItemsUpdate.subscribe((items) => {
                items[items.length - 1].isExpanded = true;
                sub.unsubscribe();
            });
        }

        if (!this.data) {
            this.data = [];
        }

        if (this.type === PatientDialogDataModel.TYPE_ORGANIZATION) {
            const newComm = new ContactCommsDataModel();
            this.data.unshift(newComm);
        } else {
            this.isLoading = true;
            if (this.entityType === PatientDialogDataModel.TYPE_DOCTOR) {
                this.doctorsService.getNewCommForContact(this.entity.dataContact.ContactId)
                    .subscribe((response) => {
                        this.isLoading = false;
                        if (response) {
                            this.data.push(response);
                        } else {
                            this.sendError('Something went wrong when adding a new communication method');
                            console.log('error, no response');
                        }
                    }, err => {
                        this.isLoading = false;
                        this.sendError('Something went wrong when adding a new communication method');
                        console.log('error', err);
                    });
            }
            else {
                this.patientService.getNewCommForContact(this.entity.dataContact.ContactId)
                    .subscribe((response) => {
                        this.isLoading = false;
                        if (response) {
                            this.data.push(response);
                        } else {
                            this.sendError('Something went wrong when adding a new communication method');
                            console.log('error, no response');
                        }
                    }, err => {
                        this.isLoading = false;
                        this.sendError('Something went wrong when adding a new communication method');
                        console.log('error', err);
                    });
            }
        }
    }

    sendError(message) {
        this.errorEvent.emit(message);
    }


    getAddressDescription(comm: ContactCommsDataModel) {
        if (this.entity) {
            let homeAddress;
            if (comm.enumCommLocationID && comm.enumCommLocationID.toUpperCase() === '0000000A-1000-0000-0000-000000000001') {
                homeAddress = this.entity.dataContact.dataAddresses.find
                    (p => p.enumAddressCategoryID === '00000008-1000-0000-0000-000000000001') as AddressDataModel;
            } else if (comm.enumCommLocationID && comm.enumCommLocationID.toUpperCase() === '0000000A-1000-0000-0000-000000000002') {
                homeAddress = this.entity.dataContact.dataAddresses.find
                    (p => p.enumAddressCategoryID === '00000008-1000-0000-0000-000000000002') as AddressDataModel;
            }

            if (homeAddress) {
                homeAddress.addrDescription = homeAddress.Address1 + ' ' + homeAddress.City + ', ' + homeAddress.State + ' ' + homeAddress.PostCode;
                return homeAddress;
            } else {
                return null;
            }
        }
        return null;
    }

    onCommMethodChange(event: EnumValidValueItemModel, communicationObj: ContactCommsDataModel) {
        if (this.commMethodEnums && communicationObj.enumCommMethodID != event.ValidValueItemId) {
            communicationObj.enumCommMethodID = event.ValidValueItemId;
            const enu = this.commMethodEnums.find(p => p.ValidValueItemId === communicationObj.enumCommMethodID);
            if (enu) {
                communicationObj.typeDescription = enu.ItemDisplayValue;
            }
            communicationObj.Address = '';
        }
    }

    onCommLocationChange(event: EnumValidValueItemModel, communicationObj: ContactCommsDataModel) {
        if (event !== undefined) {
            communicationObj.enumCommLocationID = event.ValidValueItemId;
        }
    }

    onAddressChange(event: any, communicationObj: ContactCommsDataModel) {
        communicationObj.Address = event;
    }

    changeIsPreferred(event: MatCheckboxChange, communicationObj: ContactCommsDataModel) {

        if (event.checked && communicationObj.isOptOut) {
            this.dialogService.openConfirmDialog('This patient opted out of this type of communication.<br/> Would you like to overwrite patient choice?').subscribe((result) => {

                if (result) {
                    communicationObj.isOptOut = false;
                    communicationObj.isOptOutOverwritten = true;
                }
                else {
                    communicationObj.isPreferred = false;
                    communicationObj.isOptOutOverwritten = true;
                }

            });
        }

    }
}
